import { Component, type ErrorInfo, type ReactNode } from "react";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Props {
  children: ReactNode;
}

interface State {
  hasChunkError: boolean;
  otherError: Error | null;
}

const RELOAD_FLAG = "tirevault_chunk_reload_at";
const RELOAD_COOLDOWN_MS = 10_000;

/**
 * Detects the various shapes a failed dynamic-import error can take across
 * Vite/Rollup, browsers and CDNs. We treat them all as "stale deploy — reload".
 */
const isChunkLoadError = (err: unknown): boolean => {
  if (!err) return false;
  const e = err as { name?: string; message?: string; code?: string };
  const msg = (e.message ?? String(err)).toLowerCase();
  return (
    e.name === "ChunkLoadError" ||
    e.code === "CSS_CHUNK_LOAD_FAILED" ||
    msg.includes("dynamically imported module") ||
    msg.includes("failed to fetch dynamically imported module") ||
    msg.includes("importing a module script failed") ||
    msg.includes("loading chunk") ||
    msg.includes("loading css chunk") ||
    msg.includes("error loading dynamically imported module")
  );
};

export class ChunkErrorBoundary extends Component<Props, State> {
  state: State = { hasChunkError: false, otherError: null };

  static getDerivedStateFromError(error: Error): State {
    if (isChunkLoadError(error)) {
      return { hasChunkError: true, otherError: null };
    }
    return { hasChunkError: false, otherError: error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (isChunkLoadError(error)) {
      // Auto-reload once per 10s window to recover from a fresh deploy
      // without trapping the user in a refresh loop if the new bundle is
      // genuinely broken.
      try {
        const last = Number(sessionStorage.getItem(RELOAD_FLAG) ?? "0");
        if (Date.now() - last > RELOAD_COOLDOWN_MS) {
          sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
          window.location.reload();
          return;
        }
      } catch {
        /* sessionStorage blocked → fall through to manual reload UI */
      }
      console.warn("[ChunkErrorBoundary] chunk failed to load", error, info.componentStack);
    } else {
      console.error("[ChunkErrorBoundary] runtime error", error, info.componentStack);
    }
  }

  private handleReload = () => {
    try {
      sessionStorage.removeItem(RELOAD_FLAG);
    } catch {
      /* ignore */
    }
    window.location.reload();
  };

  render() {
    if (this.state.hasChunkError) {
      return (
        <div className="flex min-h-screen items-center justify-center px-6 bg-background">
          <div className="max-w-md text-center space-y-5">
            <div className="mx-auto h-12 w-12 rounded-full bg-secondary flex items-center justify-center">
              <RefreshCw className="h-6 w-6 text-foreground-muted" />
            </div>
            <div className="space-y-2">
              <h1 className="text-xl font-semibold text-foreground">Ny version tillgänglig</h1>
              <p className="text-sm text-foreground-muted">
                En del av appen kunde inte laddas. Det beror oftast på en ny version eller tillfälligt
                avbruten uppkoppling. Ladda om sidan för att fortsätta.
              </p>
            </div>
            <Button onClick={this.handleReload} className="w-full sm:w-auto">
              <RefreshCw className="h-4 w-4 mr-2" />
              Ladda om sidan
            </Button>
          </div>
        </div>
      );
    }

    if (this.state.otherError) {
      // Non-chunk errors propagate so any outer boundary / monitoring can see them.
      throw this.state.otherError;
    }

    return this.props.children;
  }
}